import type { ContentLibraryInput, FormDSLContext } from '@commenda/types';

/**
 * Checks if a value is empty (undefined, null, empty string, or empty array).
 */
export function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
}

/**
 * Coerces a value to match the expected type.
 * e.g., wraps a single string in an array if expectedType is 'string[]'
 */
export function coerceValue(value: unknown, expectedType?: string): unknown {
  if (!expectedType) return value;

  // If expected type is an array type (ends with [])
  if (expectedType.endsWith('[]')) {
    // If value is already an array, return as-is
    if (Array.isArray(value)) return value;
    // Otherwise wrap single value in array
    return [value];
  }

  // If expected type is 'number' and value is string, try to convert
  if (expectedType === 'number' && typeof value === 'string') {
    const num = Number(value);
    return isNaN(num) ? value : num;
  }

  return value;
}

/**
 * Resolves Content Library inputs from various sources.
 * Returns null if any required input is missing.
 * Inputs are considered required if input.required === true.
 */
export function resolveInputs(
  inputs: ContentLibraryInput[] | undefined,
  dslContext: FormDSLContext | undefined,
  formData: Record<string, unknown>
): Record<string, unknown> | null {
  if (!inputs || inputs.length === 0) {
    return {};
  }

  const resolved: Record<string, unknown> = {};

  for (const input of inputs) {
    let value: unknown;
    const isRequired = input.required === true;

    switch (input.source) {
      case 'static':
        value = input.value;
        if (!isEmpty(value)) {
          resolved[input.key] = coerceValue(value, input.expectedType);
        }
        break;

      case 'context':
        if (input.contextPath && dslContext) {
          value = dslContext[input.contextPath];
        }
        if (isEmpty(value)) {
          if (isRequired) {
            return null;
          }
          break;
        }
        resolved[input.key] = coerceValue(value, input.expectedType);
        break;

      case 'field':
        if (input.fieldId) {
          value = formData[input.fieldId];
        }
        if (isEmpty(value)) {
          if (isRequired) {
            return null;
          }
          break;
        }
        resolved[input.key] = coerceValue(value, input.expectedType);
        break;
    }
  }

  return resolved;
}

/**
 * Creates a stable dependency key from form data based on input config.
 * Only includes values from fields that are actually used as inputs.
 */
export function createFormDataDependencyKey(
  inputs: ContentLibraryInput[] | undefined,
  formData: Record<string, unknown>
): string {
  if (!inputs) return '';

  const relevantValues: Record<string, unknown> = {};
  for (const input of inputs) {
    if (input.source === 'field' && input.fieldId) {
      relevantValues[input.fieldId] = formData[input.fieldId];
    }
  }
  return JSON.stringify(relevantValues);
}
